import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw, CheckCircle, XCircle, AlertCircle, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { API_CONFIG } from '@/config/api';
import IndexingStatus from './IndexingStatus';

interface ProcessingStep {
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  retry_count: number;
  can_retry: boolean;
  metadata?: Record<string, unknown>;
}

interface ProcessingStatus {
  video_id: string;
  overall_status: 'processing' | 'completed' | 'failed' | 'partial';
  started_at: string;
  completed_at?: string;
  progress: {
    total_steps: number;
    completed_steps: number;
    failed_steps: number;
    skipped_steps: number;
    completion_percentage: number;
  };
  steps: Record<string, ProcessingStep>;
  failed_steps: string[];
  retryable_steps: string[];
  database_status: string;
  processing_progress: number;
  processing_error?: string;
  processing_started_at?: string;
  processing_completed_at?: string;
  search_metadata?: Record<string, unknown>;
}

interface VideoProcessingStatusProps {
  videoId: string;
  onStatusChange?: (status: ProcessingStatus) => void;
}

const stepLabels: Record<string, string> = {
  frame_extraction: 'Frame Extraction',
  object_detection: 'Object Detection',
  audio_extraction: 'Audio Extraction',
  transcription: 'Transcription',
  speaker_diarization: 'Speaker Diarization',
  entity_extraction: 'Entity Extraction',
  embedding_generation: 'Embedding Generation',
  search_indexing: 'Search Indexing'
};

const stepDescriptions: Record<string, string> = {
  frame_extraction: 'Extracting video frames for analysis',
  object_detection: 'Detecting objects in video frames',
  audio_extraction: 'Extracting audio from video',
  transcription: 'Converting speech to text',
  speaker_diarization: 'Identifying different speakers',
  entity_extraction: 'Extracting named entities from text',
  embedding_generation: 'Generating search embeddings',
  search_indexing: 'Creating search indices'
};

const getStepIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'failed':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'processing':
      return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
    case 'skipped':
      return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-400" />;
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'completed':
      return <Badge variant="default" className="bg-green-100 text-green-800">Completed</Badge>;
    case 'failed':
      return <Badge variant="destructive">Failed</Badge>;
    case 'processing':
      return <Badge variant="secondary" className="bg-blue-100 text-blue-800">Processing</Badge>;
    case 'partial':
      return <Badge variant="outline" className="border-yellow-500 text-yellow-700">Partial Success</Badge>;
    default:
      return <Badge variant="outline">Unknown</Badge>;
  }
};

const VideoProcessingStatus: React.FC<VideoProcessingStatusProps> = ({
  videoId,
  onStatusChange
}) => {
  const { session } = useAuth();
  const [status, setStatus] = useState<ProcessingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Fetching processing status for video: ${videoId}`);
      
              const response = await fetch(`${API_CONFIG.BASE_URL}/upload/${videoId}/processing-status`, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log(`Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error: ${response.status} - ${errorText}`);
        throw new Error(`Failed to fetch status: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Processing status data:', data);
      
      // Check if the data has the expected structure
      if (!data.progress || !data.steps) {
        // This is a basic status response, not a detailed processing status
        const statusData = {
          video_id: data.video_id,
          overall_status: data.database_status?.toLowerCase() || 'unknown',
          started_at: data.processing_started_at || new Date().toISOString(),
          completed_at: data.processing_completed_at,
          progress: {
            total_steps: 8,
            completed_steps: data.database_status === 'COMPLETED' ? 8 : 0,
            failed_steps: 0,
            skipped_steps: 0,
            completion_percentage: data.database_status === 'COMPLETED' ? 100 : 0
          },
          steps: {},
          failed_steps: [],
          retryable_steps: [],
          database_status: data.database_status,
          processing_progress: data.processing_progress || 0,
          processing_error: data.processing_error,
          processing_started_at: data.processing_started_at,
          processing_completed_at: data.processing_completed_at,
          search_metadata: data.search_metadata
        };
        
        setStatus(statusData);
        
        // If there's an error message but we have status data, show it as a warning
        if (data.error) {
          setError(data.error);
        } else {
          setError(null);
        }
      } else {
        setStatus(data);
        setError(null);
      }
      
      onStatusChange?.(data);
    } catch (err) {
      console.error('Error fetching processing status:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch status');
    } finally {
      setLoading(false);
    }
  };

  const retryStep = async (step: string) => {
    try {
      setRetrying(step);
      
              const response = await fetch(`${API_CONFIG.BASE_URL}/upload/${videoId}/retry-step`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ step })
      });

      if (!response.ok) {
        throw new Error(`Failed to retry step: ${response.statusText}`);
      }

      // Wait a moment then refresh status
      setTimeout(() => {
        fetchStatus();
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry step');
    } finally {
      setRetrying(null);
    }
  };

  useEffect(() => {
    fetchStatus();
    
    // Poll for status updates if still processing
    const interval = setInterval(() => {
      if (status && status.overall_status === 'processing') {
        fetchStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [videoId]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span>Loading processing status...</span>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Fetching detailed processing information...
          </div>
          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
            <div className="text-xs text-blue-700">
              <div className="font-medium mb-1">What we're checking:</div>
              <ul className="space-y-1 text-blue-600">
                <li>• Video processing progress</li>
                <li>• Individual processing steps</li>
                <li>• Error details (if any)</li>
                <li>• Search indexing status</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error && !status) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert variant="destructive">
            <AlertDescription>
              <strong>Error loading processing status:</strong> {error}
            </AlertDescription>
          </Alert>
          <div className="mt-4 space-y-2">
            <p className="text-sm text-gray-600">
              This could be because:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>The video is still being uploaded</li>
              <li>The processing hasn't started yet</li>
              <li>There's a temporary connection issue</li>
              <li>The processing service is temporarily unavailable</li>
            </ul>
            <div className="mt-3 p-3 bg-amber-50 rounded-lg">
              <div className="text-xs text-amber-700">
                <div className="font-medium mb-1">What you can do:</div>
                <ul className="space-y-1 text-amber-600">
                  <li>• Wait a few minutes and try again</li>
                  <li>• Check if the video appears in your list</li>
                  <li>• Contact support if the issue persists</li>
                </ul>
              </div>
            </div>
          </div>
          <Button onClick={fetchStatus} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!status) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert>
            <AlertDescription>
              <strong>No processing status available</strong>
            </AlertDescription>
          </Alert>
          <div className="mt-4 space-y-2">
            <p className="text-sm text-gray-600">
              This video may not have started processing yet. Common reasons:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>Video upload is still in progress</li>
              <li>Processing queue is backed up</li>
              <li>Video hasn't been queued for processing</li>
              <li>Processing pipeline hasn't been initialized</li>
            </ul>
            <div className="mt-3">
              <Button onClick={fetchStatus} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Check Again
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Additional safety check for required fields
  if (!status.progress || !status.steps) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert>
            <AlertDescription>
              <strong>Processing status is incomplete</strong>
            </AlertDescription>
          </Alert>
          <div className="mt-4 space-y-2">
            <p className="text-sm text-gray-600">
              The processing status data is missing required information. This could mean:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>Processing hasn't started yet</li>
              <li>Processing pipeline is not properly initialized</li>
              <li>There's a temporary issue with the processing service</li>
            </ul>
            <div className="mt-3">
              <Button onClick={fetchStatus} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Check Again
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Processing Status</span>
          <div className="flex items-center space-x-2">
            {getStatusBadge(status?.overall_status || 'unknown')}
            <Button
              variant="outline"
              size="sm"
              onClick={fetchStatus}
              disabled={status?.overall_status === 'processing'}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Error Warning (if there's an error but we have status data) */}
        {error && status?.database_status !== 'COMPLETED' && (
          <Alert variant="destructive">
            <AlertDescription>
              <strong>Note:</strong> {error}
            </AlertDescription>
          </Alert>
        )}
        
        {/* Success Message for Completed Videos */}
        {status?.database_status === 'COMPLETED' && (
          <Alert className="border-green-200 bg-green-50 text-green-800">
            <AlertDescription>
              <strong>✓ Processing Complete!</strong> This video has been successfully processed and is ready for search indexing.
            </AlertDescription>
          </Alert>
        )}
        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Overall Progress</span>
            <span>
              {status?.database_status === 'COMPLETED' ? 100 : (status?.progress?.completion_percentage || 0)}%
            </span>
          </div>
          <Progress 
            value={status?.database_status === 'COMPLETED' ? 100 : (status?.progress?.completion_percentage || 0)} 
            className={status?.database_status === 'COMPLETED' ? 'bg-green-100' : ''}
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>{status?.progress?.completed_steps || 0} completed</span>
            <span>{status?.progress?.failed_steps || 0} failed</span>
            <span>{status?.progress?.skipped_steps || 0} skipped</span>
          </div>
        </div>

        {/* Processing Steps */}
        <div className="space-y-3">
          <h4 className="font-medium">Processing Steps</h4>
          {Object.keys(status?.steps || {}).length > 0 ? (
            Object.entries(status?.steps || {}).map(([stepKey, step]) => (
              <div key={stepKey} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  {getStepIcon(step.status)}
                  <div>
                    <div className="font-medium">{stepLabels[stepKey] || stepKey}</div>
                    <div className="text-sm text-gray-500">
                      {stepDescriptions[stepKey] || 'Processing step'}
                    </div>
                    {step.error_message && (
                      <div className="text-sm text-red-500 mt-1">
                        {step.error_message}
                      </div>
                    )}
                    {step.retry_count > 0 && (
                      <div className="text-sm text-gray-500">
                        Retries: {step.retry_count}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant={step.status === 'completed' ? 'default' : 'outline'}>
                    {step.status}
                  </Badge>
                  {step.status === 'failed' && step.can_retry && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => retryStep(stepKey)}
                      disabled={retrying === stepKey}
                    >
                      {retrying === stepKey ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        'Retry'
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-sm text-gray-500 p-3 border rounded-lg">
              <div className="font-medium">Basic Status Information</div>
              <div className="mt-2 space-y-1">
                <div>Database Status: <span className="font-medium">{status?.database_status || 'Unknown'}</span></div>
                <div>Processing Progress: <span className="font-medium">
                  {status?.database_status === 'COMPLETED' ? 100 : (status?.processing_progress || 0)}%
                </span></div>
                {status?.processing_started_at && (
                  <div>Started: <span className="font-medium">{new Date(status.processing_started_at).toLocaleString()}</span></div>
                )}
                {status?.processing_completed_at && (
                  <div>Completed: <span className="font-medium">{new Date(status.processing_completed_at).toLocaleString()}</span></div>
                )}
                {status?.database_status === 'COMPLETED' && (
                  <div className="text-green-600 font-medium">✓ Ready for search indexing</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Error Summary */}
        {status?.processing_error && (
          <Alert variant="destructive">
            <AlertDescription>
              <strong>Processing Error:</strong> {status.processing_error}
            </AlertDescription>
          </Alert>
        )}

        {/* Retry All Failed Steps */}
        {status?.retryable_steps?.length > 0 && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => {
                status.retryable_steps?.forEach(step => retryStep(step));
              }}
              disabled={retrying !== null}
            >
              {retrying ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry All Failed Steps
                </>
              )}
            </Button>
          </div>
        )}

        {/* Search Indexing Status */}
        {session?.access_token && (
          <div className="mt-6">
            <IndexingStatus 
              videoId={videoId} 
              token={session.access_token}
              onRetry={() => {
                // Refresh the parent component
                if (onStatusChange && status) {
                  onStatusChange(status);
                }
              }}
            />
          </div>
        )}

        {/* Timestamps */}
        <div className="text-xs text-gray-500 space-y-1">
          <div>Started: {status?.started_at ? new Date(status.started_at).toLocaleString() : 'Unknown'}</div>
          {status?.completed_at && (
            <div>Completed: {new Date(status.completed_at).toLocaleString()}</div>
          )}
        </div>
       </CardContent>
     </Card>
   );
 };

export default VideoProcessingStatus; 